import { omitBy, sortBy, assign, get, isEmpty, keys, omit } from 'lodash'
import { safeJSONParse, apmPaymentType } from '../utils'
import { getPaymentBillingInfo, getPaymentGatewayInfo, getCountryJson } from '../apis/payment'
import { WISE, ttrTaxStatusList, PAYMENT_ACTION_TYPE } from '../common/constant'
import { translate } from '../plugins/i18n'
import Enums from '../common/Enums'
import { COUNTRY_CODES_BY_ZUORA_COUNTRY } from '../common/regionUtils'
import {
  BILLING_INFO_ACTION_TYPES,
  BILLING_INFO_MUTATION_TYPES,
  getDefaultPayApmInfo,
  getDefaultCvvReAuthInfo,
  getDefaultAddressInfo,
  CONTACT_TYPES
} from './helper'

const RESTRICTED_COUNTRIES = ['CU', 'IR', 'KP', 'SY', 'CN']

const getDefaultPayMethodInfo = () => {
  return {
    paymentType: '',
    paymentGateway: '',
    paymentResponse: ''
  }
}

const state = {
  soldToAddress: {},
  billToAddress: {},
  addressInfo: getDefaultAddressInfo(),
  countryMap: {},
  i18nCountryMap: {},
  useCaseList: [],

  apmInfo: getDefaultPayApmInfo(),
  paymentMethodInfo: getDefaultPayMethodInfo(),
  payMethodReRenderTimes: 0,
  gatewayInfo: {},
  countryUseAdyenForCardValidation: {},
  billingInfo: {},
  adyenPaymentInfo: {},
  taxInfo: {
    vatNumber: '',
    notVatRegistered: false,
    ttrTaxExempt: ''
  }, // valid sold to tax info
  cvvReAuthInfo: getDefaultCvvReAuthInfo(),
  billingInfoConfig: {},
  selectedPaymentMethod: null,
  paymentFormReadyMap: {},
  achAuthRequestInitiated: false,
  sepaAuthRequestInitiated: false,
  withCcAccount: false,
  isInEnrollAutopayFlow: false,
}

const mutations = {
  setSoldToAddress(state, soldToAddress) {
    state.soldToAddress = soldToAddress
  },
  setBillToAddress(state, billToAddress) {
    state.billToAddress = billToAddress
  },
  setCountryMap(state, countryMap) {
    state.countryMap = countryMap
  },
  setI18nCountryMap(state, countryMap) {
    state.i18nCountryMap = countryMap
  },
  setAddressInfo(state, data) {
    state.addressInfo = data
  },
  setUsageType(state, data) {
    state.addressInfo.usageType = data
  },
  setUseCaseList(state, data) {
    state.useCaseList = data
  },

  setApmInfo(state, data) {
    state.apmInfo = assign(state.apmInfo, data)
  },
  setPaymentMethodInfo(state, data) {
    state.paymentMethodInfo = assign(state.paymentMethodInfo, data)
  },
  setGatewayInfo(state, data) {
    state.gatewayInfo = data
  },
  setCountryUseAdyenForCardValidation(state, data) {
    state.countryUseAdyenForCardValidation = data
  },
  setBillingInfo(state, data) {
    state.billingInfo = data
  },
  setAdyenPaymentInfo(state, data) {
    state.adyenPaymentInfo = data
  },
  setSelectedPaymentMethod(state, data) {
    state.selectedPaymentMethod = data
  },
  setTaxInfo(state, data) {
    state.taxInfo = data
  },
  setCvvReAuthInfo(state, data) {
    state.cvvReAuthInfo = data
  },
  setBillingInfoConfig(state, data) {
    state.billingInfoConfig = data
  },
  setPaymentFormReadyMap(state, data) {
    state.paymentFormReadyMap = data
  },
  setAchAuthRequestInitiated(state, data) {
    state.achAuthRequestInitiated = data
  },
  setSepaAuthRequestInitiated(state, data) {
    state.sepaAuthRequestInitiated = data
  },
  setIsInEnrollAutopayFlow(state, data) {
    state.isInEnrollAutopayFlow = data
  }
}

const getters = {
  addressInfo: (state) => state.addressInfo,
  countryMap: (state) => state.countryMap,
  i18nCountryMap: (state) => state.i18nCountryMap,
  useCaseList: (state) => state.useCaseList,
  countryList: (state) => {
    const countryMap = state.i18nCountryMap
    return sortBy(Object.keys(countryMap)).reduce((result, label) => {
      result.push({ label, value: countryMap[label] })
      return result
    }, [])
  },
  apmInfo: (state) => state.apmInfo,
  paymentMethodInfo: (state) => state.paymentMethodInfo,
  curPaymentMethod: (state) =>
    state.apmInfo.isDefaultPayment
      ? apmPaymentType(state.apmInfo.apmPaymentMethodName, state.apmInfo.tileImageFileName)
      : state.paymentMethodInfo?.paymentType,
  withCcAccount: (state) => state.billingInfoConfig.withCcAccount,
  withOutCcAccount: (state) => !state.billingInfoConfig.withCcAccount,
  payMethodReRenderTimes: (state) => state.payMethodReRenderTimes,
  gatewayInfo: (state) => state.gatewayInfo,
  countryUseAdyenForCardValidation: (state) => state.countryUseAdyenForCardValidation,
  billingInfo: (state) => state.billingInfo,
  soldToBaseContact: (state, getters) => {
    const soldToContactInfo = getters.billingInfo?.soldToContact
    const contactSame = getters.billingInfo?.soldToSameToBillTo
    const soldContact = (isEmpty(soldToContactInfo) ? getters.account : soldToContactInfo) || {}

    return {
      phone: soldContact.phone || soldContact.workPhone,
      firstName: soldContact.firstName,
      lastName: soldContact.lastName,
      email: soldContact.email || soldContact?.workEmail,
      additionalEmail: soldContact.additionalEmail__c || '',
      contactSame,
      type: CONTACT_TYPES.SOLD_TO
    }
  },
  soldToContact: (state, getters) => {
    const obj = {
      notVatRegistered: getters.billingInfo?.notVatRegistered === 'true',
      vatNumber: getters.billingInfo?.vatNumber || ''
    }
    return {
      ...getters.soldToBaseContact,
      ...omit(getters.addressInfo, ['coppa', 'phone', 'contactSame']),
      ...obj
    }
  },
  soldToAddress: (state, getters) => {
    if (getters.withCcAccount) {
      return getters.soldToContact
    }
    return state.soldToAddress
  },
  billToAddress: (state, getters) => {
    if (getters.withCcAccount) {
      const billToContactInfo = getters.billingInfo?.billToContact || {}
      const isSameAsSoldTo = getters.billingInfo?.soldToSameToBillTo
      const baseInfo = {
        phone: billToContactInfo.phone || billToContactInfo.workPhone,
        firstName: billToContactInfo.firstName,
        lastName: billToContactInfo.lastName,
        email: billToContactInfo.email || billToContactInfo.workEmail,
        contactSame: isSameAsSoldTo,
        type: CONTACT_TYPES.BILLING_TO
      }
      // companyName and employeeCount are from soldTo !!!
      const { companyName, employeeCount } = getters.addressInfo
      const accountHq = billToContactInfo.accountHq || ''
      const additionalFields = {
        companyName,
        employeeCount,
        accountHq
      }
      const obj = getters.billingInfo?.billToContact || {}
      const address = {
        address1: obj.address1,
        address2: obj.address2,
        zip: obj.postalCode,
        city: obj.city,
        state: obj.state,
        countryName: obj.country,
        country: getters.billingInfo?.billToCountryCode,
        doingBusinessAsName: obj.doingBusinessAsName
      }
      return {
        ...baseInfo,
        ...additionalFields,
        ...address
      }
    }
    return state.billToAddress
  },
  taxInfo: (state) => state.taxInfo,
  isTaxExempt: (state) =>
    state.taxInfo.ttrTaxExempt === ttrTaxStatusList.EXEMPT ||
    (state.taxInfo.vatNumber && !state.taxInfo.notVatRegistered), // US/CA support currently (TTR)
  adyenPaymentInfo: (state) => state.adyenPaymentInfo,
  cvvReAuthInfo: (state) => state.cvvReAuthInfo,

  zuoraInfo: (state) => state.zuoraInfo,
  billingInfoConfig: (state) => state.billingInfoConfig,
  getHpmPassthroughValForBackupPayment: (_, getters) => {
    return (action) => {
      if (PAYMENT_ACTION_TYPE.ADD === action && !getters.billingInfo.hasBackupPaymentMethod) {
        return 'addBackupPaymentMethod'
      }
      return 'editBackupPaymentMethod'
    }
  },
  selectedPaymentMethod: (state) => state.selectedPaymentMethod,
  paymentFormReadyMap: (state) => state.paymentFormReadyMap,
  isAchEligible: (state) => {
    const bankTransferObj = state.billingInfoConfig.bankTransfer
    return bankTransferObj?.achEnabled && bankTransferObj?.achEligible
  },
  isSepaEligible: (state) => state.billingInfoConfig.bankTransfer?.sepaEligible,
  achAuthRequestInitiated: (state) => state.achAuthRequestInitiated,
  sepaAuthRequestInitiated: (state) => state.sepaAuthRequestInitiated,
  // not saved contact before, will be new customer
  isNewCustomer: (getters) => {
    return isEmpty(getters.billingInfo?.soldToContact)
  },
  isApmEnabled: () => get(window, 'newBillingConfig.apmBillingManagementEnabled', false),
  isInEnrollAutopayFlow: (state) => state.isInEnrollAutopayFlow
}

const actions = {
  async fetchCountryMap({ commit }) {
    try {
      let data = await getCountryJson('en-US')
      // data's format is {countryName:countryId}, like {china: cn}
      // need do filter, restrict some forbidden countries
      data = omitBy(data, function (value) {
        return RESTRICTED_COUNTRIES.includes(value)
      })
      commit('setCountryMap', data)
    } catch (e) {
      console.error('get country map error')
    }
  },
  async fetchI18nCountryMap({ commit }) {
    try {
      let data = await getCountryJson()
      // data's format is {countryName:countryId}, like {china: cn}
      // need do filter, restrict some forbidden countries
      data = omitBy(data, function (value) {
        return RESTRICTED_COUNTRIES.includes(value)
      })
      commit('setI18nCountryMap', data)
    } catch (e) {
      console.error('get country map error')
    }
  },
  updateAddressInfo({ commit }, { key, value }) {
    commit('setAddressInfo', { [key]: value })
  },

  // Update address info according to sold to info
  updateAddressInfoFromSoldTo({ commit, getters }, soldTo = {}) {
    if (soldTo?.address1 || soldTo?.country) {
      const addressInfo = getters.addressInfo
      const _form = {}
      keys(soldTo).forEach((k) => {
        if (k in addressInfo) _form[k] = soldTo[k]
      })

      _form['zip'] = soldTo['postalCode']
      _form['country'] = new Enums(getters.countryMap)[soldTo.country] || COUNTRY_CODES_BY_ZUORA_COUNTRY[soldTo.country]
      _form['countryName'] = translate(`country.${_form['country']}`)
      _form['usageType'] = addressInfo?.usageType
      commit('setAddressInfo', { ...addressInfo, ..._form })
    }
  },
  updateAdyenPaymentInfo({ commit }, adyenPaymentInfo) {
    const info = {
      adyenPayConfigData: safeJSONParse(adyenPaymentInfo.adyenPayConfigData),
      adyenPaymentMethodsObject: safeJSONParse(adyenPaymentInfo.adyenPaymentMethodsObject),
      adyenPaymentMethodsEnabled: safeJSONParse(adyenPaymentInfo.adyenPaymentMethodsEnabled)
    }
    commit('setAdyenPaymentInfo', info)
  },
  updateTaxInfo({ getters, commit }) {
    const { notVatRegistered, taxExemptStatus, vatNumber } = getters.billingInfo
    const taxInfo = {
      vatNumber,
      notVatRegistered: notVatRegistered === 'true',
      ttrTaxExempt: taxExemptStatus === 'Yes' ? ttrTaxStatusList.EXEMPT : null
    }
    commit('setTaxInfo', taxInfo)
  },
  async getPaymentBillingInfo({ commit, dispatch }) {
    const { result } = await getPaymentBillingInfo()
    const billingInfo = result?.billingInfo || {}
    const { soldToContact = '', isExternalAPMMethod = false } = billingInfo
    const { name: companyName, employeeCount, usageType } = billingInfo

    if (!billingInfo.dfPaymentMethod) {
      billingInfo.paymentMethodType = null
    }

    commit('setBillingInfo', billingInfo)
    // update current payment method type
    billingInfo.paymentMethodType && commit('setPaymentMethodInfo', { paymentType: billingInfo.paymentMethodType })

    // update apm info
    if (isExternalAPMMethod) {
      let wisePayment = ''
      if (billingInfo?.dfPaymentMethod?.payment_method_name__c === WISE) {
        const apmDetails = JSON.parse(get(billingInfo, 'externalApmPaymentDetails'))
        wisePayment = apmDetails?.wise || ''
      }
      commit('setApmInfo', {
        isApm: true,
        apmPaymentMethodName: billingInfo?.dfPaymentMethod?.payment_method_name__c,
        isDefaultPayment: true,
        defaultWisePayment: wisePayment,
        tileImageFileName: wisePayment
      })
    } else {
      commit('setApmInfo', getDefaultPayApmInfo())
    }

    // Update address info according to sold to info
    soldToContact &&
      dispatch(
        'updateAddressInfoFromSoldTo',
        Object.assign({}, soldToContact, { companyName, employeeCount, usageType })
      )

    // update tax info
    billingInfo && dispatch('updateTaxInfo')
  },
  async getPaymentGatewayInfo({ commit, dispatch }) {
    const { result } = await getPaymentGatewayInfo()
    const { gatewayInfo, adyenPaymentInfo = {}, countryUseAdyenForCardValidation } = result
    commit('setGatewayInfo', gatewayInfo)
    commit('setCountryUseAdyenForCardValidation', countryUseAdyenForCardValidation)

    // set adyen payment info (for Google Pay / Apple Pay)
    !isEmpty(adyenPaymentInfo) && dispatch('updateAdyenPaymentInfo', adyenPaymentInfo)
  },
  async getPaymentInfo({ dispatch }, isLoggedIn) {
    if (isLoggedIn) {
      return Promise.all([dispatch('getPaymentGatewayInfo'), dispatch('getPaymentBillingInfo')])
    } else {
      return Promise.all([dispatch('getPaymentGatewayInfo')])
    }
  },
  async [BILLING_INFO_ACTION_TYPES.initBillingInfoForm]({ dispatch, commit }, config) {
    return Promise.all([
      commit('setBillingInfoConfig', config),
      dispatch('fetchCountryMap'),
      dispatch('fetchI18nCountryMap'),
      dispatch('getPaymentInfo', true)
    ])
  },
  async [BILLING_INFO_ACTION_TYPES.setAddress]({ commit }, { soldToContact, billToContact }) {
    await commit('setSoldToAddress', soldToContact)
    await commit('setBillToAddress', billToContact)
  },
  paymentFormReady({ commit, state }, paymethod) {
    const paymentFormReadyMap = { ...state.paymentFormReadyMap }
    paymentFormReadyMap[paymethod] = true
    commit('setPaymentFormReadyMap', paymentFormReadyMap)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
